import { Injectable, OnDestroy } from "@angular/core";
import { PivotInstance } from "@shared/services/pivot-instance";
import {
    IFlagged,
    IFlaggedArgumentsBase,
    IFlaggedGatewayBase,
    IFlaggedWithDetails,
    ResponseMessage,
    UpdateFlagsResponseBase
} from "@shared/types";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";

export interface IFlaggablePage {
    saveStateBeforeFlagsChanged?(): unknown;
    restoreStateAfterFlagsChanged?(sourceState: unknown);
    afterFlagUpdate<T extends IFlaggedArgumentsBase>(response: ResponseMessage, input: T);
    afterBulkUpdate<T extends IFlaggedArgumentsBase>(response: ResponseMessage, input: T);
}

@Injectable()
export class SavingFlagsService implements OnDestroy {
    protected _processingFlagsStorage: string[] | true = [];

    protected flagsStorageChanged$ = new BehaviorSubject<string[] | true>([]);

    private _context: IFlaggablePage;
    private _gateway: IFlaggedGatewayBase;

    init(context: IFlaggablePage, gateway: IFlaggedGatewayBase) {
        this._context = context;
        this._gateway = gateway;
    }

    ngOnDestroy(): void {
        this.flagsStorageChanged$.complete();
    }

    public _updateFlags<T extends IFlaggedArgumentsBase, K = keyof T>(input: T, keyProp: K) {
        const key = input[keyProp.toString()];
        this._addDgFlagsToLoading(key);
        let stateStorage = {};
        if (typeof this._context.saveStateBeforeFlagsChanged === "function") {
            stateStorage = this._context.saveStateBeforeFlagsChanged();
        }
        this._gateway.updateFlag(input).subscribe({
            next: res => {
                this._context.afterFlagUpdate(res, input);
            },
            error: err => {
                this._removeDgFlagFromLoading(input[keyProp.toString()]);
            },
            complete: () => {
                if (typeof this._context.restoreStateAfterFlagsChanged === "function")
                    this._context.restoreStateAfterFlagsChanged(stateStorage);
                this._removeDgFlagFromLoading(key);
            }
        });
    }

    _bulkUpdate(input: IFlaggedArgumentsBase) {
        this._addDgFlagsToLoading(true);
        let stateStorage = {};
        if (typeof this._context.saveStateBeforeFlagsChanged === "function") {
            stateStorage = this._context.saveStateBeforeFlagsChanged();
        }
        this._gateway.updateFlag(input).subscribe({
            next: res => {
                this._context.afterBulkUpdate(res, input);
            },
            error: err => {
                this._removeDgFlagFromLoading(true);
            },
            complete: () => {
                if (typeof this._context.restoreStateAfterFlagsChanged === "function")
                    this._context.restoreStateAfterFlagsChanged(stateStorage);
                this._removeDgFlagFromLoading(true);
            }
        });
    }

    public repopulatePivotWithFlags<T extends UpdateFlagsResponseBase>(
        flags: T,
        updateCallback: (flags: T, node: IFlagged) => number | null,
        targetPivot: PivotInstance<IFlagged, unknown>
    ) {
        const newNodes: IFlaggedWithDetails[] = [];
        targetPivot.getLeavesOfNode(targetPivot.all, 0, (node: IFlaggedWithDetails) => {
            const newFlag = updateCallback(flags, node);
            if (newFlag != null) {
                newNodes.push({
                    ...node,
                    flag_id: newFlag,
                    flag_comment: flags.flag_comment,
                    flag_user_id_inserted: flags.user_id_inserted,
                    flag_date_inserted: flags.date_inserted
                });
            } else newNodes.push(node);
        });
        targetPivot.build(newNodes);
    }

    protected _addDgFlagsToLoading(keyProp: true | string): void {
        if (keyProp === true) {
            this._processingFlagsStorage = true;
        } else if (typeof this._processingFlagsStorage === "boolean") {
            return;
        } else {
            this._processingFlagsStorage.push(keyProp);
        }
        this.flagsStorageChanged$.next(this._processingFlagsStorage);
    }

    protected _removeDgFlagFromLoading(diagnoseGroupsCode: true | string): void {
        if (diagnoseGroupsCode === true) {
            this._processingFlagsStorage = [];
        } else {
            if (typeof this._processingFlagsStorage === "boolean") return;
            if (this._processingFlagsStorage.includes(diagnoseGroupsCode)) {
                this._processingFlagsStorage = this._processingFlagsStorage.filter(
                    x => x !== diagnoseGroupsCode
                );
            }
            this.flagsStorageChanged$.next(this._processingFlagsStorage);
        }
    }

    public _isDgFlagLoading(keyProp?: string): Observable<boolean> {
        return this.flagsStorageChanged$.pipe(
            map(() => {
                if (this._processingFlagsStorage === true) return true;

                if (!keyProp) return this._processingFlagsStorage.length > 0;

                return this._processingFlagsStorage.findIndex(i => i === keyProp) > -1;
            })
        );
    }
}
